import { createContext, useContext, useEffect, useState } from "react";
import { 
  type LanguageCode, 
  LANGUAGES, 
  ENABLED_LANGUAGES,
  detectBrowserLanguage as detectLang,
  isRTL,
  INTL_LOCALE,
  htmlLangFor,
} from "@shared/languageConfig";
import { setDisplayLocale } from "@/lib/format";

type LanguageProviderProps = {
  children: React.ReactNode;
  defaultLanguage?: LanguageCode;
  storageKey?: string;
};

type LanguageProviderState = {
  language: LanguageCode;
  setLanguage: (language: LanguageCode) => void;
};

const initialState: LanguageProviderState = {
  language: "en",
  setLanguage: () => null,
};

const LanguageProviderContext = createContext<LanguageProviderState>(initialState);

// Re-export from centralized config for backward compatibility
export { ENABLED_LANGUAGES as SUPPORTED_LANGUAGES };
export type { LanguageCode as Language };

// Language names for display - using centralized config
export const languageNames = Object.fromEntries(
  Object.entries(LANGUAGES).map(([code, config]) => [code, config.nativeName])
) as Record<LanguageCode, string>;

// Wrapper for browser language detection
function detectBrowserLanguage(): LanguageCode {
  return detectLang();
}

export function LanguageProvider({
  children,
  defaultLanguage = detectBrowserLanguage(),
  storageKey = "ui-language",
  ...props
}: LanguageProviderProps) {
  const [language, setLanguage] = useState<LanguageCode>(() => {
    if (typeof window === "undefined") return defaultLanguage;
    const stored = localStorage.getItem(storageKey) as LanguageCode;
    const initial = stored && LANGUAGES[stored] ? stored : defaultLanguage;
    // Before the first render, so the first dates painted are already the
    // reader's — lib/format.ts reads this module-level value.
    setDisplayLocale(INTL_LOCALE[initial] ?? "en-CA");
    return initial;
  });

  useEffect(() => {
    const root = window.document.documentElement;
    // <html lang> is the BCP 47 tag that picks the right glyphs (Cantonese is
    // zh-HK, not "yue"); the app's own code for the language rides beside it.
    root.setAttribute("lang", htmlLangFor(language));
    root.dataset.uiLanguage = language;
    
    // Set text direction based on centralized config
    root.setAttribute("dir", isRTL(language) ? "rtl" : "ltr");
  }, [language]);

  const value = {
    language,
    setLanguage: (newLanguage: LanguageCode) => {
      if (LANGUAGES[newLanguage]) {
        localStorage.setItem(storageKey, newLanguage);
        setDisplayLocale(INTL_LOCALE[newLanguage] ?? "en-CA");
        setLanguage(newLanguage);
      }
    },
  };

  return (
    <LanguageProviderContext.Provider {...props} value={value}>
      {children}
    </LanguageProviderContext.Provider>
  );
}

export const useLanguage = () => {
  const context = useContext(LanguageProviderContext);

  if (context === undefined)
    throw new Error("useLanguage must be used within a LanguageProvider");

  return context;
};
